import { computed, ref } from "vue";
// import { Store } from "vuex";
import { useState, useSessionState } from "@/store";
// import store from "@/store";
import { commitLogout } from "@/store";
import { AppState, GetUserProfileResponse, TrainingTypeEnum } from "./types";
import router from "@/router";
import { ConfirmationServiceMethods } from "primevue/confirmationservice";
import { useGtag } from "vue-gtag-next";
import axios, { AxiosError } from "axios";
import dayjs from "dayjs";
import CustomParseFormat from "dayjs/plugin/customParseFormat";
import BuddhistEra from "dayjs/plugin/buddhistEra";
import numeral from "numeral";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { User } from "@/types";
import {
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  Router,
} from "vue-router";

const state = useState();
const sessionState = useSessionState();
// const isLoggedIn = computed(() => !!store.state.token);

const setLoading = (isLoading: boolean) =>
  (sessionState.value.loadingCount += isLoading ? 1 : -1);
// localStore.commit("setLoading", isLoading);

let localToast: ReturnType<typeof useToast>;
let localRouter: Router;
let localRoute: RouteLocationNormalizedLoaded;
// let localStore: Store<AppState>;
let localConfirm: ReturnType<typeof useConfirm>;

const gtag = useGtag();

const setupAxiosInterceptor = () => {
  axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // console.log(JSON.stringify(error));
      if (error.response?.status === 401) {
        logout(true);
        if (error.response?.data !== null)
          error.response.data = {
            title: "session หมดอายุกรุณา Login ใหม่อีกครั้ง",
          };
        return Promise.reject(error);
      } else {
        //Blob handle
        if (
          error.request.responseType === "blob" &&
          error.response?.data instanceof Blob &&
          error.response?.data.type &&
          error.response?.data.type.toLowerCase().indexOf("json") != -1
        ) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              error.response!.data = JSON.parse(reader.result as string);
              resolve(Promise.reject(error));
            };
            reader.onerror = () => {
              reject(error);
            };
            reader.readAsText(error.response!.data);
          });
        }

        return Promise.reject(error);
      }
    }
  );
};

const setupPlugin = (
  toastInput: ReturnType<typeof useToast>,
  routerInput: Router,
  routeInput: RouteLocationNormalizedLoaded,
  confirmInput: ReturnType<typeof useConfirm>
) => {
  localToast = toastInput;
  localRouter = routerInput;
  localRoute = routeInput;
  localConfirm = confirmInput;

  //To handle 401
  setupAxiosInterceptor();
};

const logout = (addReturn = false) => {
  // store.commit("logout");
  commitLogout();

  if (addReturn === true) {
    router.replace({
      name: "login",
      query: {
        name: localRoute.name as string,
        params: JSON.stringify(localRoute.params),
      },
    });
  } else {
    router.replace({ name: "login" });
  }
};

const onLogout = () => {
  localConfirm.require({
    message: "กรุณายืนยันเพื่อออกจากโปรแกรม",
    header: "ออกจากระบบ",
    acceptLabel: "ออกจากระบบ",
    accept: () => logout(),
    rejectLabel: "อยู่ในระบบต่อ",
  });
};
const onBack = () => {
  localRouter.back();
};
const onRouterPush = async (route: RouteLocationRaw) => localRouter.push(route);

const onRouterReplace = async (route: RouteLocationRaw) =>
  localRouter.replace(route);
//Set up Axios.

//Set up dayjs
dayjs.extend(CustomParseFormat); //https://day.js.org/docs/en/plugin/custom-parse-format
dayjs.extend(BuddhistEra); //https://day.js.org/docs/en/plugin/buddhist-era

require("dayjs/locale/th");
dayjs.locale("th");

//toast
const life = 3000;
const success = (text: string, header?: string) =>
  localToast.add({
    summary: header ?? "สำเร็จ",
    detail: text,
    severity: "success",
    life: life,
  });
const error = (text: any, header?: string) => {
  const message =
    typeof text?.response?.data === "string"
      ? text.response.data
      : text?.response?.data?.title
      ? text?.response?.data?.title
      : text?.response?.data?.errors
      ? JSON.stringify(text?.response?.data?.errors)
      : text?.response?.data?.message || text?.message || text;
  localToast.add({
    summary: header ?? "ข้อผิดพลาด",
    detail: message,
    severity: "error",
    life: life,
  });
};

const warning = (text: string, header?: string) =>
  localToast.add({
    summary: header ?? "แจ้งเตือน",
    detail: text,
    severity: "warning",
    life: life,
  });
const information = (text: string, header?: string) =>
  localToast.add({
    summary: header ?? "ข้อมูล",
    detail: text,
    severity: "info",
    life: life,
  });

const popupCookie = () => {
  window.open("https://www.haadthip.com/en/cookie-policy", "_blank");
};

const popupPersonalPolicy = () => {
  window.open(
    "https://www.haadthip.com/storage/document/personal-info-protection/2021/htc-personal-data-protection-th.pdf",
    "_blank"
  );
};
const setIdentity = (logout = true) => {
  if (!logout) {
    axios.defaults.headers.common = {
      Authorization: "",
      Accept: "application/json, text/plain, */*",
    };
    gtag.set({ user_id: null });
  } else {
    axios.defaults.headers.common = {
      Authorization: `Bearer ${state.value.authentication.token}`,
      Accept: "application/json, text/plain, */*",
    };
    gtag.set({
      user_id: state.value.authentication.username,
    });
  }
};

const padZero = (value: any, length: number) => {
  if (isNaN(value)) return value;

  return value.padStart(length, "0");
};

const trainingTypeDesc = (trainingType: number) => {
  switch (trainingType) {
    case TrainingTypeEnum.Internal:
      return "ภายใน";
    case TrainingTypeEnum.External:
      return "ภายนอก";
    case TrainingTypeEnum.NewEmployee:
      return "พนักงานใหม่";
    case TrainingTypeEnum.Transfer:
      return "ย้ายแผนก";
    case TrainingTypeEnum.Promote:
      return "เลื่อนตำแหน่ง";
    case TrainingTypeEnum.OnTheJob:
      return "อบรมต้นสังกัด";
    case TrainingTypeEnum.SiteVisit:
      return "ศึกษาดูงาน";
  }
};
const formatDate = (date: string) => {
  // if (date)
  [date.substr(8, 2), date.substr(5, 2), date.substr(0, 4)].join("/");
};

// const searchUser = async (userId: string) => {
//   try {
//     const result = await axios.get<User[]>(
//       "https://sapmasterdev.haadthip.com/api/v1/sapmaster/employees",
//       { params: { query: userId } }
//     );
//     return result.data;
//   } finally {
//     console.log();
//   }
// };

const formatAppDate = (input: Date | string | null | undefined) => {
  if (input === null || input === undefined) return "N/A";
  return dayjs(input).format("DD MMM BBBB");
};

const formatAppDateTime = (input: Date | string | null | undefined) => {
  if (input === null || input === undefined) return "N/A";
  return dayjs(input).format("DD MMM BBBB HH:mm น.");
};

const getUserProfile = async () => {
  if (!state.value.authentication.token) return;
  setLoading(true);
  try {
    const result = await axios.get<GetUserProfileResponse>(
      "api/v1/services/profile"
    );
    // localStore.commit("setProfile", result.data.profile);
    if (result.data.profile) sessionState.value.profile = result.data.profile;
    // if (result.data.profile?.isPdpaAccepted !== true) {
    //   onRouterReplace({ name: "pdpa" });
    // }
  } catch (e) {
    error(e);
  } finally {
    setLoading(false);
  }
};

const isAdmin = () => sessionState.value.profile.isAdmin;

export const usePlugin = () => {
  return {
    // isLoggedIn,
    setupPlugin,

    onLogout,
    onBack,
    onRouterPush,
    onRouterReplace,

    setLoading,
    route: localRoute,
    router: localRouter,
    confirmation: localConfirm,

    setIdentity,
    getUserProfile,
    gtag,

    axios,
    numeral,
    dayjs,

    success,
    error,
    information,
    warning,

    padZero,
    popupPersonalPolicy,
    popupCookie,
    trainingTypeDesc,
    formatDate,
    formatAppDate,
    formatAppDateTime,
    useState,
    useSessionState,

    isAdmin,
    // searchUser,
  };
};
